import { z } from 'zod';

/**
 * The document model.
 *
 * A Document is data + a sequence of page instances, each rendered by a
 * ComponentDef. Two loop mechanisms, learned from the BCE proposal shapes:
 *
 *  - PAGE loop (`repeat` on a PageInstance): the page renders once per item
 *    in a data array — e.g. one program page per opportunity line item.
 *  - COMPONENT loop (`{{#each path}}…{{/each}}` inside a component source):
 *    a block inside one page repeats — e.g. FAQ entries, TOC rows.
 *
 * Components are HTML with {{bindings}} today; `kind: 'react'` components run
 * in the sandboxed preview iframe (see apps/web/src/sandbox).
 */

export const COMPONENT_KIND = { html: 'html', react: 'react' } as const;
export type ComponentKind = (typeof COMPONENT_KIND)[keyof typeof COMPONENT_KIND];

export const componentDefSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  kind: z.enum([COMPONENT_KIND.html, COMPONENT_KIND.react]),
  /** HTML with {{bindings}}, or TSX source for sandboxed react components. */
  source: z.string(),
  /** JSON-schema-ish description of the props the component expects. Drives
   *  the structured-output prompt when the AI composes documents. */
  propsSchema: z.record(z.string(), z.unknown()).optional(),
});
export type ComponentDef = z.infer<typeof componentDefSchema>;

export const pageRepeatSchema = z.object({
  /** dot-path into the document data, must resolve to an array */
  over: z.string().min(1),
  /** name the current item is exposed under inside the page scope */
  as: z.string().min(1),
});
export type PageRepeat = z.infer<typeof pageRepeatSchema>;

export const pageInstanceSchema = z.object({
  id: z.string().min(1),
  title: z.string().optional(),
  componentId: z.string().min(1),
  /** static props merged into the render scope for this page */
  props: z.record(z.string(), z.unknown()).optional(),
  /** page-level loop: render this page once per item */
  repeat: pageRepeatSchema.optional(),
});
export type PageInstance = z.infer<typeof pageInstanceSchema>;

export const documentSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** the theme stylesheet applied to the whole document */
  css: z.string(),
  /** arbitrary data tree that bindings and loops resolve against */
  data: z.record(z.string(), z.unknown()),
  pages: z.array(pageInstanceSchema),
  updatedAt: z.string().optional(),
});
export type Document = z.infer<typeof documentSchema>;

export const documentBundleSchema = z.object({
  document: documentSchema,
  components: z.array(componentDefSchema),
});
export type DocumentBundle = z.infer<typeof documentBundleSchema>;
